import React, { useState, useEffect } from 'react';
import { X, Bookmark, Tag, FileText, Check, AlertCircle } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

/**
 * Dialog zum Speichern des aktuellen Warenkorbs als Vorlage.
 * Felder: Name (Pflicht), Tags (kommagetrennt -> CSV-String), Notiz, Checkbox „Als Vorlage".
 * Gibt onSave(payload) zurück; payload entspricht dem POST /api/saved-carts Body-Contract.
 */
const SaveCartModal = ({ isOpen, onClose, onSave, itemCount = 0, totalAmount = '0.00' }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [form, setForm] = useState({ name: '', note: '', tags: '', is_template: false });
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setForm({ name: '', note: '', tags: '', is_template: false });
      setNameError('');
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
    if (k === 'name') setNameError('');
  };

  const handleSave = async () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      setNameError(t('savedCarts.modal.nameRequired'));
      return;
    }

    setSubmitting(true);
    try {
      await onSave({
        name: trimmedName,
        note: form.note.trim(),
        tags: form.tags.trim(),
        is_template: form.is_template ? 1 : 0,
      });
      onClose();
    } catch (err) {
      // Fehler wird vom Aufrufer via showError behandelt
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`;
  const inputCls = `w-full rounded-xl px-3 py-2 text-sm border focus:ring-2 focus:ring-brand/40 focus:outline-none transition-colors ${
    isDarkMode
      ? 'bg-gray-800/40 text-white border-white/10 placeholder-white/30'
      : 'bg-white text-slate-800 border-slate-200 placeholder-slate-300'
  }`;
  const inputErrorCls = `w-full rounded-xl px-3 py-2 text-sm border focus:ring-2 focus:ring-red-400/40 focus:outline-none transition-colors ${
    isDarkMode
      ? 'bg-gray-800/40 text-white border-red-500/50 placeholder-white/30'
      : 'bg-white text-slate-800 border-red-400 placeholder-slate-300'
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 ${isDarkMode ? 'bg-black/60 backdrop-blur-sm' : 'bg-slate-900/30 backdrop-blur-sm'}`}
        onClick={onClose}
      />

      {/* Dialog */}
      <div className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
        isDarkMode ? 'bg-gray-900 border-white/10' : 'bg-white border-slate-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-brand/20' : 'bg-brand/10'}`}>
              <Bookmark className="w-5 h-5 text-brand" />
            </div>
            <div>
              <h3 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {t('savedCarts.modal.title')}
              </h3>
              <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                {t('savedCarts.modal.subtitle', { count: itemCount, total: totalAmount })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10 text-white/60' : 'hover:bg-slate-100 text-slate-400'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className={labelCls}>
              {t('savedCarts.modal.nameLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder={t('savedCarts.modal.namePlaceholder')}
              className={nameError ? inputErrorCls : inputCls}
              maxLength={255}
              autoFocus
            />
            {nameError && (
              <div className="flex items-center gap-1 mt-1">
                <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-500">{nameError}</p>
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className={`${labelCls} flex items-center gap-1`}>
              <Tag className="w-3 h-3" />
              {t('savedCarts.modal.tagsLabel')}
            </label>
            <input
              type="text"
              value={form.tags}
              onChange={set('tags')}
              placeholder={t('savedCarts.modal.tagsPlaceholder')}
              className={inputCls}
              maxLength={500}
            />
            <p className={`text-[11px] mt-1 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
              {t('savedCarts.modal.tagsHint')}
            </p>
          </div>

          {/* Notiz */}
          <div>
            <label className={`${labelCls} flex items-center gap-1`}>
              <FileText className="w-3 h-3" />
              {t('savedCarts.modal.noteLabel')}
            </label>
            <textarea
              value={form.note}
              onChange={set('note')}
              placeholder={t('savedCarts.modal.notePlaceholder')}
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </div>

          {/* Vorlage-Checkbox */}
          <label className={`flex items-start gap-3 cursor-pointer rounded-xl px-3 py-3 transition-colors ${
            isDarkMode
              ? 'hover:bg-white/5 border border-white/10'
              : 'hover:bg-slate-50 border border-slate-200'
          }`}>
            <div className="relative flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={form.is_template}
                onChange={set('is_template')}
                className="sr-only"
              />
              <div className={`w-4 h-4 rounded flex items-center justify-center transition-colors ${
                form.is_template
                  ? 'bg-brand border-brand'
                  : isDarkMode
                    ? 'bg-gray-700 border border-white/20'
                    : 'bg-white border border-slate-300'
              }`}>
                {form.is_template && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>
            <div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
                {t('savedCarts.modal.templateLabel')}
              </span>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                {t('savedCarts.modal.templateHint')}
              </p>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <button
            onClick={onClose}
            disabled={submitting}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              isDarkMode ? 'text-white/70 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || !form.name.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-brand hover:bg-[#1D4ED8] disabled:opacity-60 transition-colors"
          >
            <Bookmark className="w-4 h-4" />
            {submitting ? t('savedCarts.modal.saving') : t('savedCarts.modal.saveBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveCartModal;
